import { useState, useEffect, useMemo, useRef } from 'react'
import * as Sentry from '@sentry/react'
import Button from '../components/ui/Button'
import { ChartSkeleton } from '../components/Skeleton'
import { getAllStats, getCoverageHeatmap, getCo2Report, downloadCo2ReportPdf } from '../api/reports'
import { useToast } from '../components/Toast'
import { listTrucks } from '../api/tankguard'
import { useSettings } from '../api/settings'
import { useIsMobile } from '../hooks/useIsMobile'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const HEAT_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.heat/0.2.0/leaflet-heat.js'

// One-shot loader for the leaflet.heat plugin. The plugin attaches itself to
// the global L when it parses, so we resolve once the script tag fires onload.
function loadHeatPlugin() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'))
  if (window.L && typeof window.L.heatLayer === 'function') return Promise.resolve()
  if (window.__tgHeatPromise) return window.__tgHeatPromise
  window.__tgHeatPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = HEAT_CDN
    s.async = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Failed to load leaflet.heat'))
    document.head.appendChild(s)
  })
  return window.__tgHeatPromise
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function fmt(n, dec = 0) {
  return Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: dec, minimumFractionDigits: dec })
}

function aggDaily(data) {
  const map = new Map()
  data.forEach(s => {
    const key = String(s.date).slice(0, 10)
    if (!map.has(key)) map.set(key, { fuel: 0, dist: 0 })
    const d = map.get(key)
    d.fuel += parseFloat(s.fuel_consumed_l || 0)
    d.dist += parseFloat(s.distance_km || 0)
  })
  return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([date, vals]) => ({ date, ...vals }))
}

function CalIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--tg-text-muted)', flexShrink: 0 }}>
      <rect x="4" y="5" width="16" height="16" rx="2" /><path d="M4 9h16M9 3v4M15 3v4" />
    </svg>
  )
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12M8 11l4 4 4-4M5 21h14" />
    </svg>
  )
}

function AlertTriIcon() {
  return (
    <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="var(--tg-danger-500)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto' }}>
      <path d="M10.36 3.59L1.99 17.5A1.91 1.91 0 0 0 3.63 20.4h16.74a1.91 1.91 0 0 0 1.64-2.9L13.64 3.6a1.91 1.91 0 0 0-3.28 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  )
}

function DownArrow() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 19V5M5 12l7 7 7-7" />
    </svg>
  )
}

function UpArrow() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12l7-7 7 7" />
    </svg>
  )
}

function LineChart({ cur, prev, labels, id }) {
  // Empty state: brand-new org, or a date range entirely before the first day
  // of telemetry. Treat all-zero data the same way — drawing a flat line at 0
  // is misleading because it looks like real readings.
  const hasAnyData = cur.some(v => v > 0) || prev.some(v => v > 0)
  if (cur.length < 2 || !hasAnyData) {
    return (
      <div style={{ padding: 'var(--tg-space-4)' }}>
        <ChartSkeleton height={180} />
        <div style={{ textAlign: 'center', marginTop: 12, color: 'var(--tg-text-secondary)', fontSize: 'var(--tg-text-sm)', fontWeight: 500 }}>
          No data yet for this period
        </div>
        <div style={{ textAlign: 'center', marginTop: 4, color: 'var(--tg-text-muted)', fontSize: 'var(--tg-text-xs)' }}>
          Check back after the first day of operation.
        </div>
      </div>
    )
  }

  const W = 520, H = 240
  const padL = 8, padR = 8, padT = 14, padB = 22
  const max = Math.max(...cur, ...prev, 1) * 1.12
  const X = i => padL + (i / (cur.length - 1)) * (W - padL - padR)
  const Y = v => padT + (1 - v / max) * (H - padT - padB)
  const linePath = arr => arr.map((v, i) => (i ? 'L' : 'M') + X(i).toFixed(1) + ' ' + Y(v).toFixed(1)).join(' ')
  const curPath = linePath(cur)
  const areaPath = curPath + ` L${X(cur.length - 1).toFixed(1)} ${H - padB} L${X(0).toFixed(1)} ${H - padB} Z`
  const gradId = `teal-area-${id}`

  const gridLines = []
  for (let g = 0; g <= 3; g++) {
    const gy = padT + (g / 3) * (H - padT - padB)
    const gv = max * (1 - g / 3)
    gridLines.push({ gy, gv, g })
  }

  const xLabelIdxs = [0, Math.floor(cur.length / 2), cur.length - 1]

  return (
    <div className="chart-area">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '100%', overflow: 'visible' }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="var(--tg-teal-500)" stopOpacity="0.16" />
            <stop offset="1" stopColor="var(--tg-teal-500)" stopOpacity="0" />
          </linearGradient>
        </defs>
        {gridLines.map(({ gy, gv, g }) => (
          <g key={g}>
            <line x1={padL} y1={gy.toFixed(1)} x2={W - padR} y2={gy.toFixed(1)} stroke="var(--tg-border-default)" strokeWidth="1" />
            <text x={W - padR} y={(gy - 4).toFixed(1)} textAnchor="end" fontSize="10" fill="var(--tg-text-muted)" fontFamily="var(--tg-font-mono)">{Math.round(gv)}</text>
          </g>
        ))}
        {xLabelIdxs.map(idx => (
          <text key={idx} x={X(idx).toFixed(1)} y={H - 6}
            textAnchor={idx === 0 ? 'start' : idx === cur.length - 1 ? 'end' : 'middle'}
            fontSize="10" fill="var(--tg-text-muted)">{labels[idx]}</text>
        ))}
        <path d={areaPath} fill={`url(#${gradId})`} />
        <path d={linePath(prev)} fill="none" stroke="var(--tg-graphite-400)" strokeWidth="1.75" strokeDasharray="4 4" strokeLinecap="round" strokeLinejoin="round" />
        <path d={curPath} fill="none" stroke="var(--tg-teal-500)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  )
}

function downloadCsv(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

const RANGES = [7, 30, 90]

export default function Reports() {
  const settings = useSettings()
  const toast = useToast()
  const DIESEL = settings.diesel
  const [stats, setStats] = useState([])
  const [trucks, setTrucks] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState('')
  const [range, setRange] = useState(30)
  const [heatRange, setHeatRange] = useState(30)
  const [heatLoading, setHeatLoading] = useState(false)
  const [heatErr, setHeatErr] = useState('')
  const [heatData, setHeatData] = useState(null)
  const heatMapEl = useRef(null)
  const heatMapRef = useRef(null)
  const heatLayerRef = useRef(null)
  const now = new Date()
  const [co2Year, setCo2Year] = useState(now.getUTCFullYear())
  const [co2Month, setCo2Month] = useState(now.getUTCMonth() + 1)
  const [co2Data, setCo2Data] = useState(null)
  const [co2Loading, setCo2Loading] = useState(false)
  const [co2Err, setCo2Err] = useState('')
  const [co2Downloading, setCo2Downloading] = useState(false)

  async function loadData() {
    setLoading(true); setLoadErr('')
    try {
      const [s, t] = await Promise.all([getAllStats(), listTrucks()])
      setStats(s); setTrucks(t)
    } catch (e) { setLoadErr(e.message); Sentry.captureException(e) }
    setLoading(false)
  }

  useEffect(() => { loadData() }, [])

  // CO2/ESG: refetch whenever the month picker changes. Errors surface inline.
  useEffect(() => {
    let cancelled = false
    setCo2Loading(true); setCo2Err('')
    getCo2Report(co2Year, co2Month)
      .then(d => { if (!cancelled) setCo2Data(d) })
      .catch(e => { if (!cancelled) { setCo2Err(e.message); Sentry.captureException(e) } })
      .finally(() => { if (!cancelled) setCo2Loading(false) })
    return () => { cancelled = true }
  }, [co2Year, co2Month])

  async function downloadCo2Pdf() {
    setCo2Downloading(true)
    try { await downloadCo2ReportPdf(co2Year, co2Month); toast.success('ESG report PDF downloaded') }
    catch (e) { toast.error('Download failed', e.message); Sentry.captureException(e) }
    setCo2Downloading(false)
  }

  // Fetch heatmap data when the heatmap range changes.
  useEffect(() => {
    let cancelled = false
    setHeatLoading(true); setHeatErr('')
    getCoverageHeatmap(heatRange)
      .then(d => { if (!cancelled) setHeatData(d) })
      .catch(e => { if (!cancelled) { setHeatErr(e.message); Sentry.captureException(e) } })
      .finally(() => { if (!cancelled) setHeatLoading(false) })
    return () => { cancelled = true }
  }, [heatRange])

  // Initialise the Leaflet map exactly once (the heatLayer is replaced on each
  // data refresh). Loads the leaflet.heat CDN script on first render.
  useEffect(() => {
    let cancelled = false
    loadHeatPlugin()
      .then(() => {
        if (cancelled || !heatMapEl.current || heatMapRef.current) return
        const map = L.map(heatMapEl.current, { center: [49.0, 10.0], zoom: 6, scrollWheelZoom: false })
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap' }).addTo(map)
        heatMapRef.current = map
      })
      .catch(err => { if (!cancelled) setHeatErr(err.message) })
    return () => { cancelled = true }
  }, [])

  // Render / replace the heat layer whenever new points arrive.
  useEffect(() => {
    const map = heatMapRef.current
    if (!map || !heatData || !window.L || typeof window.L.heatLayer !== 'function') return
    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current)
      heatLayerRef.current = null
    }
    if (!heatData.points || heatData.points.length === 0) return
    const max = Math.max(...heatData.points.map(p => p[2]))
    heatLayerRef.current = window.L.heatLayer(heatData.points, {
      radius: 22, blur: 18, maxZoom: 14, max: max || 1,
      gradient: { 0.2: '#2dd4bf', 0.4: '#facc15', 0.7: '#f97316', 1.0: '#dc2626' },
    }).addTo(map)
    // Fit bounds the first time data lands so the user sees their footprint.
    const lats = heatData.points.map(p => p[0])
    const lngs = heatData.points.map(p => p[1])
    map.fitBounds([[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]], { padding: [24, 24] })
  }, [heatData])

  const currentFiltered = useMemo(() => {
    const cutoff = Date.now() - range * 86400000
    return stats.filter(s => new Date(s.date).getTime() >= cutoff)
  }, [stats, range])

  const prevFiltered = useMemo(() => {
    const cutoff = Date.now() - range * 86400000
    const prevCutoff = Date.now() - range * 2 * 86400000
    return stats.filter(s => {
      const t = new Date(s.date).getTime()
      return t >= prevCutoff && t < cutoff
    })
  }, [stats, range])

  const currentDaily = useMemo(() => aggDaily(currentFiltered), [currentFiltered])
  const prevDaily = useMemo(() => aggDaily(prevFiltered), [prevFiltered])

  const { fuelCur, fuelPrev, distCur, distPrev, labels } = useMemo(() => {
    const today = new Date()
    const labels = []
    for (let d = range - 1; d >= 0; d--) {
      const dt = new Date(today)
      dt.setDate(today.getDate() - d)
      labels.push(MONTHS[dt.getMonth()] + ' ' + dt.getDate())
    }
    const curMap = new Map(currentDaily.map(d => [d.date, d]))
    const prevMap = new Map(prevDaily.map(d => [d.date, d]))
    const fuelCur = [], fuelPrev = [], distCur = [], distPrev = []
    for (let d = range - 1; d >= 0; d--) {
      const dt = new Date(today)
      dt.setDate(today.getDate() - d)
      const dateKey = dt.toISOString().slice(0, 10)
      const cur = curMap.get(dateKey)
      fuelCur.push(cur ? cur.fuel : 0)
      distCur.push(cur ? cur.dist : 0)
      const prevDt = new Date(today)
      prevDt.setDate(today.getDate() - d - range)
      const prevKey = prevDt.toISOString().slice(0, 10)
      const prev = prevMap.get(prevKey)
      fuelPrev.push(prev ? prev.fuel : 0)
      distPrev.push(prev ? prev.dist : 0)
    }
    return { fuelCur, fuelPrev, distCur, distPrev, labels }
  }, [currentDaily, prevDaily, range])

  const totals = useMemo(() => currentFiltered.reduce((acc, s) => ({
    distance: acc.distance + parseFloat(s.distance_km || 0),
    fuelConsumed: acc.fuelConsumed + parseFloat(s.fuel_consumed_l || 0),
    fuelAdded: acc.fuelAdded + parseFloat(s.fuel_added_l || 0),
    theftLoss: acc.theftLoss + parseFloat(s.theft_loss_l || 0),
    theftEvents: acc.theftEvents + parseInt(s.theft_events || 0),
    trips: acc.trips + parseInt(s.trip_count || 0),
  }), { distance: 0, fuelConsumed: 0, fuelAdded: 0, theftLoss: 0, theftEvents: 0, trips: 0 }), [currentFiltered])

  const prevTotals = useMemo(() => prevFiltered.reduce((acc, s) => ({
    fuelConsumed: acc.fuelConsumed + parseFloat(s.fuel_consumed_l || 0),
    distance: acc.distance + parseFloat(s.distance_km || 0),
  }), { fuelConsumed: 0, distance: 0 }), [prevFiltered])

  const perTruck = useMemo(() => {
    const map = new Map()
    trucks.forEach(t => map.set(t.id, { ...t, distance: 0, fuelConsumed: 0, theftLoss: 0, theftEvents: 0, refuels: 0 }))
    currentFiltered.forEach(s => {
      const t = map.get(s.truck_id)
      if (!t) return
      t.distance += parseFloat(s.distance_km || 0)
      t.fuelConsumed += parseFloat(s.fuel_consumed_l || 0)
      t.theftLoss += parseFloat(s.theft_loss_l || 0)
      t.theftEvents += parseInt(s.theft_events || 0)
      t.refuels += parseInt(s.refuel_count || 0)
    })
    return Array.from(map.values()).map(t => ({
      ...t,
      efficiency: t.distance > 1 ? Math.round((t.fuelConsumed / t.distance) * 1000) / 10 : null,
    }))
  }, [currentFiltered, trucks])

  const mobile = useIsMobile()

  const today = new Date()
  const rangeStart = new Date(today)
  rangeStart.setDate(today.getDate() - range)
  const dateLabel = MONTHS[rangeStart.getMonth()] + ' ' + rangeStart.getDate() + ' – ' + MONTHS[today.getMonth()] + ' ' + today.getDate() + ', ' + today.getFullYear()

  const fuelDelta = prevTotals.fuelConsumed > 0 ? ((totals.fuelConsumed - prevTotals.fuelConsumed) / prevTotals.fuelConsumed * 100) : null
  const distDelta = prevTotals.distance > 0 ? ((totals.distance - prevTotals.distance) / prevTotals.distance * 100) : null
  const totalFuel = fuelCur.reduce((a, b) => a + b, 0)
  const totalDist = distCur.reduce((a, b) => a + b, 0)

  function exportCsv() {
    const truckMap = new Map(trucks.map(t => [t.id, t.name]))
    const rows = [['Date', 'Truck', 'Distance (km)', 'Fuel consumed (L)', 'Fuel added (L)', 'Trips', 'Theft loss (L)', 'Theft events']]
    currentFiltered.forEach(s => {
      rows.push([
        String(s.date).slice(0, 10),
        truckMap.get(s.truck_id) || s.truck_id,
        parseFloat(s.distance_km || 0).toFixed(1),
        parseFloat(s.fuel_consumed_l || 0).toFixed(1),
        parseFloat(s.fuel_added_l || 0).toFixed(1),
        s.trip_count || 0,
        parseFloat(s.theft_loss_l || 0).toFixed(1),
        s.theft_events || 0,
      ])
    })
    const csv = rows.map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\r\n')
    downloadCsv(csv, `tankguard-report-${range}d-${today.toISOString().slice(0, 10)}.csv`)
  }

  const fleetTotals = perTruck.reduce((a, t) => ({
    fuel: a.fuel + t.fuelConsumed,
    dist: a.dist + t.distance,
    refuels: a.refuels + (t.refuels || 0),
    theft: a.theft + t.theftEvents,
  }), { fuel: 0, dist: 0, refuels: 0, theft: 0 })

  return (
    <div className="reports-page">
      <div className="page-head">
        <div>
          <h1>Reports</h1>
          <p>Fuel and distance across your fleet · comparison vs. previous period</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--tg-space-3)', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 36, padding: '0 14px', fontSize: 'var(--tg-text-base)', color: 'var(--tg-text-secondary)', background: 'var(--tg-bg-surface)', border: '1px solid var(--tg-border-default)', borderRadius: 'var(--tg-radius-lg)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            <CalIcon />
            {dateLabel}
          </span>
          <div className="range-selector">
            {RANGES.map(r => (
              <button key={r} className={range === r ? 'is-active' : ''} onClick={() => setRange(r)}>{r}d</button>
            ))}
          </div>
          <Button variant="secondary" size="sm" onClick={exportCsv}>
            <DownloadIcon />
            CSV
          </Button>
        </div>
      </div>

      {loading && (
        <div style={{ padding: '40px', color: 'var(--tg-text-muted)', fontSize: 'var(--tg-text-sm)', textAlign: 'center' }}>Loading reports…</div>
      )}

      {!loading && loadErr && (
        <div style={{ padding: '60px 24px', textAlign: 'center' }}>
          <AlertTriIcon />
          <div style={{ fontWeight: 'var(--tg-weight-medium)', color: 'var(--tg-text-primary)', fontSize: 'var(--tg-text-md)', marginBottom: 6, marginTop: 12 }}>Couldn't load reports</div>
          <div style={{ color: 'var(--tg-text-secondary)', fontSize: 'var(--tg-text-sm)', marginBottom: 20 }}>{loadErr}</div>
          <Button variant="primary" onClick={loadData}>Try again</Button>
        </div>
      )}

      {!loading && !loadErr && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 'var(--tg-space-4)', marginBottom: 'var(--tg-space-4)' }}>
            <div className="chart-card">
              <div className="chart-card__head">
                <div>
                  <h3 className="chart-card__title">Fuel consumed</h3>
                  <div className="chart-card__total">{fmt(Math.round(totalFuel))} <span className="u">L</span></div>
                  {fuelDelta !== null && (
                    <div className={`cc-delta ${fuelDelta <= 0 ? 'down' : 'up'}`}>
                      {fuelDelta <= 0 ? <DownArrow /> : <UpArrow />}
                      {Math.abs(fuelDelta).toFixed(1)}% vs. previous
                    </div>
                  )}
                </div>
                <div className="chart-legend">
                  <span className="li"><span className="ln cur" />This period</span>
                  <span className="li"><span className="ln prev" />Previous</span>
                </div>
              </div>
              <LineChart cur={fuelCur} prev={fuelPrev} labels={labels} id="fuel" />
            </div>
            <div className="chart-card">
              <div className="chart-card__head">
                <div>
                  <h3 className="chart-card__title">Distance driven</h3>
                  <div className="chart-card__total">{fmt(Math.round(totalDist))} <span className="u">km</span></div>
                  {distDelta !== null && (
                    <div className={`cc-delta ${distDelta >= 0 ? 'up' : 'down'}`}>
                      {distDelta >= 0 ? <UpArrow /> : <DownArrow />}
                      {Math.abs(distDelta).toFixed(1)}% vs. previous
                    </div>
                  )}
                </div>
                <div className="chart-legend">
                  <span className="li"><span className="ln cur" />This period</span>
                  <span className="li"><span className="ln prev" />Previous</span>
                </div>
              </div>
              <LineChart cur={distCur} prev={distPrev} labels={labels} id="dist" />
            </div>
          </div>

          <div className="tbl-card">
            <div className="tbl-head">
              <h2>Per-truck breakdown</h2>
              <span style={{ fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-tertiary)' }}>{range}d · {trucks.length} truck{trucks.length !== 1 ? 's' : ''}</span>
            </div>
            <table className="bd">
              <thead>
                <tr>
                  <th>Truck</th>
                  <th className="r">Fuel consumed</th>
                  <th className="r">Distance</th>
                  {!mobile && <th className="r">Efficiency</th>}
                  {!mobile && <th className="r">Refuels</th>}
                  <th className="r">Theft events</th>
                </tr>
              </thead>
              <tbody>
                {perTruck.length === 0 && (
                  <tr><td colSpan={mobile ? 4 : 6} style={{ textAlign: 'center', color: 'var(--tg-text-muted)', padding: '30px 16px' }}>No truck data for this period</td></tr>
                )}
                {perTruck.map(t => {
                  const dotCls = t.status === 'active' ? 'on' : t.status === 'disabled' ? 'off' : 'on'
                  return (
                    <tr key={t.id}>
                      <td><span className="nm"><span className={`dot ${dotCls}`} />{t.name}</span></td>
                      <td className="r">{fmt(t.fuelConsumed, 1)} L</td>
                      <td className="r">{fmt(t.distance, 1)} km</td>
                      {!mobile && <td className="r">{t.efficiency != null ? t.efficiency.toFixed(1) + ' L/100km' : '—'}</td>}
                      {!mobile && <td className="r">{t.refuels || '—'}</td>}
                      <td className="r"><span className={`theft-cell${t.theftEvents > 0 ? ' has' : ''}`}>{t.theftEvents || '—'}</span></td>
                    </tr>
                  )
                })}
              </tbody>
              {perTruck.length > 0 && (
                <tfoot>
                  <tr>
                    <td>Fleet total</td>
                    <td className="r">{fmt(fleetTotals.fuel, 1)} L</td>
                    <td className="r">{fmt(fleetTotals.dist, 1)} km</td>
                    {!mobile && <td className="r">{fleetTotals.dist > 1 ? (fleetTotals.fuel / fleetTotals.dist * 100).toFixed(1) + ' L/100km' : '—'}</td>}
                    {!mobile && <td className="r">{fleetTotals.refuels || '—'}</td>}
                    <td className="r">{fleetTotals.theft || '—'}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="tbl-card" style={{ marginTop: 'var(--tg-space-4)' }}>
            <div className="tbl-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--tg-space-3)' }}>
              <div>
                <h2>CO₂ &amp; ESG report</h2>
                <div style={{ fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-tertiary)', marginTop: 2 }}>
                  Monthly emissions for EU CSRD reporting · IPCC factor 2.68 kg CO₂/L diesel
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--tg-space-3)', flexWrap: 'wrap' }}>
                <select className="tg-input" style={{ width: 110, appearance: 'auto' }} value={co2Month} onChange={e => setCo2Month(parseInt(e.target.value))}>
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <select className="tg-input" style={{ width: 100, appearance: 'auto', fontFamily: 'var(--tg-font-mono)' }} value={co2Year} onChange={e => setCo2Year(parseInt(e.target.value))}>
                  {[now.getUTCFullYear(), now.getUTCFullYear() - 1, now.getUTCFullYear() - 2].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <Button variant="primary" size="sm" onClick={downloadCo2Pdf} loading={co2Downloading} disabled={co2Downloading || !co2Data || co2Loading}>
                  <DownloadIcon />
                  ESG report PDF
                </Button>
              </div>
            </div>
            <div style={{ padding: 'var(--tg-space-5)' }}>
              {co2Loading && <div style={{ color: 'var(--tg-text-muted)', fontSize: 'var(--tg-text-sm)' }}>Loading CO₂ data…</div>}
              {!co2Loading && co2Err && <div style={{ color: 'var(--tg-danger-700)', fontSize: 'var(--tg-text-sm)' }}>Couldn't load CO₂ report: {co2Err}</div>}
              {!co2Loading && !co2Err && co2Data && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr 1fr', gap: 'var(--tg-space-4)' }}>
                    <div style={{ padding: 'var(--tg-space-4)', background: 'var(--tg-bg-surface)', border: '1px solid var(--tg-border-default)', borderRadius: 'var(--tg-radius-lg)' }}>
                      <div style={{ fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total CO₂</div>
                      <div style={{ fontSize: 28, fontWeight: 600, color: 'var(--tg-text-primary)', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                        {fmt(co2Data.fleet.total_co2_tonnes, 2)} <span style={{ fontSize: 14, color: 'var(--tg-text-tertiary)' }}>t</span>
                      </div>
                      <div style={{ fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)', marginTop: 4 }}>
                        {fmt(Math.round(co2Data.fleet.total_co2_kg))} kg
                      </div>
                    </div>
                    <div style={{ padding: 'var(--tg-space-4)', background: 'var(--tg-bg-surface)', border: '1px solid var(--tg-border-default)', borderRadius: 'var(--tg-radius-lg)' }}>
                      <div style={{ fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Carbon cost</div>
                      <div style={{ fontSize: 28, fontWeight: 600, color: 'var(--tg-text-primary)', fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
                        € {fmt(co2Data.fleet.carbon_cost_eur, 2)}
                      </div>
                      <div style={{ fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)', marginTop: 4 }}>
                        €{fmt(co2Data.carbon_price_eur_tonne, 2)}/t · EU ETS
                      </div>
                    </div>
                    <div style={{ padding: 'var(--tg-space-4)', background: 'var(--tg-bg-surface)', border: '1px solid var(--tg-border-default)', borderRadius: 'var(--tg-radius-lg)' }}>
                      <div style={{ fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>vs last month</div>
                      {co2Data.vs_last_period.co2_change_pct === null ? (
                        <div style={{ fontSize: 28, fontWeight: 600, color: 'var(--tg-text-muted)', marginTop: 4 }}>—</div>
                      ) : (
                        <div className={`cc-delta ${co2Data.vs_last_period.co2_change_pct <= 0 ? 'down' : 'up'}`} style={{ marginTop: 4, fontSize: 22 }}>
                          {co2Data.vs_last_period.co2_change_pct <= 0 ? <DownArrow /> : <UpArrow />}
                          {Math.abs(co2Data.vs_last_period.co2_change_pct).toFixed(1)}%
                        </div>
                      )}
                      <div style={{ fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)', marginTop: 4 }}>
                        vs {co2Data.vs_last_period.prev_period}
                      </div>
                    </div>
                  </div>
                  <table className="bd" style={{ marginTop: 'var(--tg-space-4)' }}>
                    <thead>
                      <tr>
                        <th>Truck</th>
                        <th className="r">Fuel (L)</th>
                        <th className="r">Distance (km)</th>
                        <th className="r">CO₂ (kg)</th>
                        <th className="r">CO₂ (t)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {co2Data.trucks.length === 0 && (
                        <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--tg-text-muted)', padding: '24px 16px' }}>No emissions data for this month</td></tr>
                      )}
                      {co2Data.trucks.map(t => (
                        <tr key={t.truck_id}>
                          <td><span className="nm">{t.name}</span></td>
                          <td className="r">{fmt(Math.round(t.fuel_l))}</td>
                          <td className="r">{fmt(Math.round(t.km))}</td>
                          <td className="r">{fmt(Math.round(t.co2_kg))}</td>
                          <td className="r">{t.co2_tonnes.toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>

          <div className="tbl-card" style={{ marginTop: 'var(--tg-space-4)' }}>
            <div className="tbl-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--tg-space-3)' }}>
              <div>
                <h2>Fleet coverage</h2>
                <div style={{ fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-tertiary)', marginTop: 2 }}>
                  GPS density across the fleet · last {heatRange} days
                  {heatData && heatData.point_count > 0 && ' · ' + fmt(heatData.point_count) + ' grid cells'}
                </div>
              </div>
              <div className="range-selector">
                {RANGES.map(r => (
                  <button key={r} className={heatRange === r ? 'is-active' : ''} onClick={() => setHeatRange(r)}>{r}d</button>
                ))}
              </div>
            </div>
            <div style={{ position: 'relative', height: 420, borderTop: '1px solid var(--tg-border-default)' }}>
              <div ref={heatMapEl} style={{ height: '100%', width: '100%' }} />
              {heatLoading && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.65)', fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-secondary)', pointerEvents: 'none' }}>
                  Loading coverage…
                </div>
              )}
              {!heatLoading && heatData && heatData.point_count === 0 && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.85)', fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-secondary)', pointerEvents: 'none', textAlign: 'center', padding: 24 }}>
                  No GPS data in the selected window.
                </div>
              )}
              {heatErr && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.85)', color: 'var(--tg-danger-700)', fontSize: 'var(--tg-text-sm)', pointerEvents: 'none', textAlign: 'center', padding: 24 }}>
                  Couldn't load coverage map: {heatErr}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
